import { AddCustomerIcon, AddJobIcon, EstimatesTabIcon, InvoicesTabIcon } from 'assets/icons';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectCurrentTeamInvoicePermissions,
  selectCurrentTeamJobPermissions,
  selectIsOwner,
  showNotifier
} from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { CustomButton } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { clevertapEvents } from 'types/baseTypes';
import { InfoMesssages } from 'types/infoMessages';
import { NotifierTypes } from 'constants/notifierConstants';
import { ButtonType } from 'components/custom-button/CustomButton';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { resetCreateJobState } from 'containers/jobs/create-edit-job/createJobSlice';
import { SubscriptionStatus } from 'containers/app/app.model';
import { useCommunicationsControl } from 'containers/communications';

interface QuickActionsProps {
  className?: string;
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
  isTabView?: boolean;
}

const QuickActions: FC<QuickActionsProps> = ({ className = '', onPrimaryAction, onSecondaryAction, isTabView }) => {
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const jobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);
  const { allowAction: allowInvoiceCreate } = useCommunicationsControl('INVOICE_CREATE');
  const { allowAction: allowJobCreate } = useCommunicationsControl('JOB_CREATE');
  const { allowAction: allowEstimateCreate } = useCommunicationsControl('ESTIMATE_CREATE');

  const onPrimaryActionClick = () => {
    logAnalyticEvent(clevertapEvents.WebHomeCreateInvoice);
    if (!invoicePermissions?.createInvoice || !customerPermissions.createNewCustomers) {
      const toastId = isTabView ? 'quick-actions-create-invoice-button' : 'home-quick-actions-create-invoice-button';
      onInfoClick(toastId, InfoMesssages.createInvoiceNoPermissionMessage);
      return;
    }
    if (!allowInvoiceCreate()) return;
    onPrimaryAction();
  };

  const onSecondaryActionClick = () => {
    logAnalyticEvent(clevertapEvents.WebHomeCreateCustomer);
    if (!customerPermissions.createNewCustomers) {
      const toastId = isTabView ? 'quick-actions-create-customer-button' : 'home-quick-actions-create-customer-button';
      onInfoClick(toastId, InfoMesssages.createCustomerNoPermissionMessage);
    } else onSecondaryAction();
  };

  const onCreateJobClick = () => {
    if (!allowJobCreate()) return;
    dispatch(resetCreateJobState());
    logAnalyticEvent(clevertapEvents.webHomeCreateJob);
    navigate(routesPath.CREATE_JOB);
  };

  const onCreateEstimateClick = () => {
    if (!invoicePermissions?.createInvoice || !customerPermissions.createNewCustomers) {
      const toastId = isTabView ? 'quick-actions-create-estimate-button' : 'home-quick-actions-create-estimate-button';
      onInfoClick(toastId, InfoMesssages.genericNoPermissionMessage);
      return;
    }
    if (
      currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED ||
      currentTeam?.subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED
    ) {
      if (isOwner) navigate(routesPath.ESTIMATE_INTEGRATION);
      else navigate(routesPath.ESTIMATES);
      return;
    }
    if (!allowEstimateCreate()) return;

    navigate(routesPath.ESTIMATES_CREATE);
  };

  const onInfoClick = (id: string, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  return (
    <div className={twMerge('mt-7 mb-2 flex flex-col', className)}>
      <div className="text-xl font-bold text-primaryText">Quick actions</div>
      <div className="flex flex-col sm:w-52 ">
        <CustomButton
          id={`${isTabView ? 'quick-actions-create-invoice-button' : 'home-quick-actions-create-invoice-button'}`}
          onClick={onPrimaryActionClick}
          className={'opacity:100 mt-4 h-10 w-52 text-17px text-primaryBtnText'}
          StartIcon={InvoicesTabIcon}
          startIconStyle="path-stroke-current"
          type={ButtonType.PRIMARY}
          showDisabledReason={!invoicePermissions.createInvoice || !customerPermissions.createNewCustomers}>
          <div className="text-sbase font-semibold">Create invoice</div>
        </CustomButton>

        <CustomButton
          id={`${isTabView ? 'quick-actions-create-job-button' : 'home-quick-actions-create-job-button'}`}
          onClick={onCreateJobClick}
          className={'opacity:100  mt-4 h-10 w-52 text-17px text-primaryBtnText'}
          type={ButtonType.PRIMARY}
          StartIcon={AddJobIcon}
          showDisabledReason={!jobPermissions.createAndEditJob}
          disabledReason={InfoMesssages.createJobNoPermissionMessage}>
          <div className="text-sbase font-semibold">Create job</div>
        </CustomButton>

        <CustomButton
          id={`${isTabView ? 'quick-actions-create-estimate-button' : 'home-quick-actions-create-estimate-button'}`}
          onClick={onCreateEstimateClick}
          className={'opacity:100  mt-4 h-10 w-52 text-17px text-primaryBtnText'}
          type={ButtonType.PRIMARY}
          StartIcon={EstimatesTabIcon}
          startIconStyle="path-fill-current">
          <div className="text-sbase font-semibold">Create estimate</div>
        </CustomButton>

        <CustomButton
          id={`${isTabView ? 'quick-actions-create-customer-button' : 'home-quick-actions-create-customer-button'}`}
          onClick={onSecondaryActionClick}
          className={'opacity:100  mt-4 h-10 w-52 text-17px text-primaryBtnText'}
          type={ButtonType.PRIMARY}
          StartIcon={AddCustomerIcon}
          showDisabledReason={!customerPermissions.createNewCustomers}>
          <div className="text-sbase font-semibold">Create customer</div>
        </CustomButton>
      </div>
    </div>
  );
};

export default QuickActions;
